/**
 * Location Disclosure Gate
 *
 * Central choke-point for every one-shot GPS request in the app. Ensures the
 * in-app `LocationDisclosureModal` is shown exactly once per device before any
 * location-permission prompt is triggered, satisfying Google Play's prominent-
 * disclosure requirement and giving users a consistent explanation of why the
 * app needs their location — regardless of which feature asked first.
 *
 * The disclosure is purely informational: its only action is "Continue", which
 * always proceeds to the OS permission prompt (Apple Guideline 5.1.1(iv)). The
 * user grants or denies access at the system dialog, not in our modal.
 *
 * Background tracking (`useWaypointTracking` / `trackingController`) uses the
 * same `DISCLOSURE_KEY` flag, so accepting once covers both foreground and
 * background use.
 *
 * Usage (foreground, with UI):
 *   const loc = await requestLocationWithDisclosure({ enableHighAccuracy: true });
 *   if (loc) { ...use loc.coords... }
 *
 * Usage (no UI available — e.g. push handler):
 *   if (canUseLocationSilently()) { ...call locationService directly... }
 */
import locationService, { type LocationResult } from './location';

export const DISCLOSURE_KEY = 'location_disclosure_accepted';

export function hasAcceptedLocationDisclosure(): boolean {
  try {
    return localStorage.getItem(DISCLOSURE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setLocationDisclosureAccepted(): void {
  try {
    localStorage.setItem(DISCLOSURE_KEY, '1');
  } catch {
    /* storage unavailable — accept for this session only */
  }
}

/**
 * Non-interactive check. Returns true only if the user has previously seen and
 * acknowledged the disclosure. Use from contexts where no modal can be shown
 * (push-notification receivers, background handlers).
 */
export function canUseLocationSilently(): boolean {
  return hasAcceptedLocationDisclosure();
}

// ---------------------------------------------------------------------------
// Event bus — LocationDisclosureHost listens for this and resolves the promise
// ---------------------------------------------------------------------------

const DISCLOSURE_EVENT = 'location-disclosure-requested';

let pendingResolver: (() => void) | null = null;

function promptForDisclosure(): Promise<void> {
  // If another prompt is already pending, resolve the previous one so we never
  // hold a dangling promise.
  if (pendingResolver) {
    pendingResolver();
    pendingResolver = null;
  }
  return new Promise<void>((resolve) => {
    pendingResolver = resolve;
    window.dispatchEvent(new CustomEvent(DISCLOSURE_EVENT));
  });
}

/**
 * Internal — called by LocationDisclosureHost when the user taps "Continue".
 * Not part of the public API; consumers should use requestLocationWithDisclosure.
 */
export function _resolvePendingDisclosure(): void {
  if (pendingResolver) {
    pendingResolver();
    pendingResolver = null;
  }
}

export const LOCATION_DISCLOSURE_EVENT = DISCLOSURE_EVENT;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch a single position fix. If the user has not yet seen the location
 * disclosure, the modal is shown first; tapping "Continue" proceeds straight to
 * the OS permission prompt.
 *
 * Returns null when:
 *  - the OS permission prompt is denied
 *  - the device has no geolocation support
 *  - the position request times out or fails
 *
 * Never throws. Callers should treat null as "no location available" and
 * continue (clock-in, sign-off etc. all tolerate a null position).
 */
export async function requestLocationWithDisclosure(
  options?: PositionOptions,
): Promise<LocationResult | null> {
  if (!hasAcceptedLocationDisclosure()) {
    await promptForDisclosure();
    setLocationDisclosureAccepted();
  }
  try {
    return await locationService.getCurrentPosition(options);
  } catch {
    return null;
  }
}
